import {
  useQuery,
  useInfiniteQuery,
  keepPreviousData,
} from '@tanstack/react-query';
import { api } from 'src/services/api';

export type PatientSearchResult = {
  appointment_date: string | null;
  ehr_id: string | null;
  next_appointment_date: string | null;
  past_appointment_date: string | null;
  patient_Name: string;
  patient_birth_date: string;
  patient_id: number;
};

export type SearchPatientsResponse = {
  has_next: boolean;
  has_prev: boolean;
  page: number;
  pages: number;
  patients: PatientSearchResult[];
  per_page: number;
  total: number;
};

export type UseSearchPatientsParams = {
  searchTerm: string;
  page?: number;
  perPage?: number;
  enabled?: boolean;
};

export const useSearchPatients = ({
  searchTerm,
  page = 1,
  perPage = 20,
  enabled = true,
}: UseSearchPatientsParams) => {
  return useQuery({
    queryKey: ['searchPatients', searchTerm, page, perPage],
    queryFn: () =>
      api
        .get<SearchPatientsResponse>('/v2/patients/search', {
          params: {
            search_term: searchTerm,
            page,
            per_page: perPage,
          },
        })
        .then((res) => res.data),
    enabled: enabled && !!searchTerm && searchTerm.length >= 3,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 mins
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useSearchPatientsInfinite = ({
  searchTerm,
  perPage = 20,
  enabled = true,
}: Omit<UseSearchPatientsParams, 'page'>) => {
  return useInfiniteQuery({
    queryKey: ['searchPatientsInfinite', searchTerm, perPage],
    queryFn: ({ pageParam = 1 }) =>
      api
        .get<SearchPatientsResponse>('/v2/patients/search', {
          params: {
            search_term: searchTerm,
            page: pageParam,
            per_page: perPage,
          },
        })
        .then((res) => res.data),
    getNextPageParam: (lastPage) => {
      if (lastPage.has_next) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: enabled && !!searchTerm && searchTerm.length >= 3,
    staleTime: 1000 * 60 * 5, // 5 mins
    refetchOnWindowFocus: false,
    retry: false,
  });
};
